// Data for the stock page's daily and weekly chart: all stored candles (/v1/candles, range max: the
// breakout's 52-week and all-time high need the full history, as in the backtest), weekly momentum ranks
// (/v1/momentum) and the new-high breakout setup computed from the daily candles, exactly as tested.
import { useEffect, useMemo, useState } from 'react';
import { chartPatterns, highBreakouts, type Breakout, type ChartPattern } from '@planb/core';
import type { Candle, Overlays } from '@/charts/history-chart';
import { api } from '@/lib/api';

export interface MomentumRank { date: string; rank: number; of: number; score: number }
export interface ChartHistory {
  daily: Candle[];
  weekly: Candle[];
  monthly: Candle[];
  ranks: MomentumRank[];
  breakouts: Breakout[];
  /** Classic chart patterns in the daily candles (research spec A11, as tested). */
  patterns: ChartPattern[];
  loading: boolean;
  error: boolean;
}
const dayT = (date: string) => Date.parse(`${date}T00:00:00Z`) / 1000;
const isoOf = (t: number) => new Date(t * 1000).toISOString().slice(0, 10);

/** Fetches once per symbol and again when `refresh` changes (a new 5-minute bar while live). */
export function useChartHistory(symbol: string, enabled: boolean, refresh: number): ChartHistory {
  const [state, setState] = useState<ChartHistory>({ daily: [], weekly: [], monthly: [], ranks: [], breakouts: [], patterns: [], loading: false, error: false });
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    setState((s) => ({ ...s, loading: true }));
    const get = (path: string) => api(path).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))));
    const q = encodeURIComponent(symbol);
    Promise.all([get(`/v1/candles?symbol=${q}&tf=1D&range=max`), get(`/v1/candles?symbol=${q}&tf=1W&range=max`), get(`/v1/candles?symbol=${q}&tf=1M&range=max`), get(`/v1/momentum?symbol=${q}`)])
      .then(([d, w, mo, m]) => {
        if (stop) return;
        const daily: Candle[] = d.candles ?? [];
        const days = daily.map((c) => ({ date: isoOf(c.t), o: c.o, h: c.h, l: c.l, c: c.c, v: c.v }));
        setState({ daily, weekly: w.candles ?? [], monthly: mo.candles ?? [], ranks: m.history ?? [], breakouts: highBreakouts(days), patterns: chartPatterns(days), loading: false, error: false });
      })
      .catch(() => { if (!stop) setState((s) => ({ ...s, loading: false, error: true })); });
    return () => { stop = true; };
  }, [symbol, enabled, refresh]);
  return state;
}

/** The overlays for the daily, weekly or monthly candles (each point lands on the candle containing it). */
export function useOverlays(h: ChartHistory, kind: 'daily' | 'weekly' | 'monthly', showPatterns: boolean): Overlays {
  const weekly = kind !== 'daily';
  return useMemo(() => {
    const candles = kind === 'monthly' ? h.monthly : kind === 'weekly' ? h.weekly : h.daily;
    const starts = candles.map((c) => c.t);
    // The candle containing time t: the last candle starting at or before it.
    const slot = (t: number) => { let lo = 0, hi = starts.length - 1, ans = -1; while (lo <= hi) { const mid = (lo + hi) >> 1; if (starts[mid] <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1; } return ans; };
    const at = (t: number) => { const i = weekly ? slot(t) : starts.indexOf(t); return i >= 0 ? starts[i] : null; };
    const marks: Overlays['marks'] = [];
    const stopByT = new Map<number, number>();
    for (const b of h.breakouts) {
      const e = at(h.daily[b.entryIdx].t); if (e !== null) marks.push({ t: e, kind: 'entry' });
      if (b.exitIdx !== null) { const x = at(h.daily[b.exitIdx].t); if (x !== null) marks.push({ t: x, kind: 'exit' }); }
      const end = b.exitIdx ?? h.daily.length;
      for (let i = b.entryIdx; i < end; i++) {
        const v = i === h.daily.length - 1 && b.exitIdx === null ? b.stopNow : b.stop[i - b.entryIdx];
        // A stop at or below zero (10 x ATR under the price early in a long trend) is no stop: it isn't
        // drawn, else the price axis stretches below 0.
        const t = at(h.daily[i].t); if (t !== null && v !== undefined && v > 0) stopByT.set(t, v); // weekly: the week's last value
      }
    }
    const stop = starts.map((t) => ({ t, v: stopByT.get(t) ?? null }));
    const rankByT = new Map<number, number>();
    for (const r of h.ranks) { const t = at(dayT(r.date)) ?? (weekly ? null : dayT(r.date)); if (t !== null && starts.includes(t)) rankByT.set(t, r.rank); }
    const rank = [...rankByT].sort((a, b) => a[0] - b[0]).map(([t, v]) => ({ t, v }));
    marks.sort((a, b) => a.t - b.t);
    // Patterns on daily and weekly candles (on monthly ones a pattern collapses into a candle or two).
    const patterns = !showPatterns || kind === 'monthly' ? [] : h.patterns.flatMap((p) => {
      const pt = (i: number, v: number) => { const t = at(h.daily[i]?.t ?? NaN); return t === null ? null : { t, v }; };
      const pts = p.points.map((x) => pt(x.i, x.p)), level = [pt(p.level.from.i, p.level.from.p), pt(p.level.to.i, p.level.to.p)];
      if (pts.some((x) => !x) || level.some((x) => !x)) return [];
      return [{ name: p.name, pts: pts as { t: number; v: number }[], level: level as { t: number; v: number }[], breakT: p.breakIdx === null ? null : at(h.daily[p.breakIdx].t) }];
    });
    return { marks, stop, rank, rankOf: h.ranks.length ? h.ranks[h.ranks.length - 1].of : 0, patterns };
  }, [h, kind, weekly, showPatterns]);
}

/** Plain-language status of the two tested setups for the latest session. */
export function setupStatus(h: ChartHistory) {
  const last = h.daily[h.daily.length - 1];
  const b = h.breakouts[h.breakouts.length - 1];
  const fmt = (t: number) => new Date(t * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  let breakout: string;
  if (!last) breakout = '';
  else if (b && b.exitIdx === null) {
    const gap = (last.c - b.stopNow) / last.c * 100;
    breakout = `In a new-high trend since ${fmt(h.daily[b.entryIdx].t)} · trailing stop ${b.stopNow.toLocaleString('en-IN', { maximumFractionDigits: 2 })} (${gap.toFixed(1)}% below the last close)`;
  } else if (b && b.exitIdx !== null) breakout = `No open new-high trend · the last one ended ${fmt(h.daily[b.exitIdx].t)}`;
  else breakout = 'No new 52-week-and-all-time-high breakout in the data';
  const r = h.ranks[h.ranks.length - 1];
  let momentum = '';
  if (r) {
    let weeks = 0; for (let i = h.ranks.length - 1; i >= 0 && h.ranks[i].rank <= 20; i--) weeks++;
    const yearBest = Math.min(...h.ranks.slice(-52).map((x) => x.rank));
    momentum = `Momentum #${r.rank} of ${r.of} this week` + (weeks ? ` · in the top 20 for ${weeks} week${weeks > 1 ? 's' : ''}` : ` · best in the last year #${yearBest}`);
  }
  return { breakout, momentum };
}

/** Past sessions' intraday candles at tf over `range` (the API's ranges), plus today's. */
export function useIntradayHistory(symbol: string, tf: string, range: string, enabled: boolean, refresh: number) {
  // `key` says which symbol and timeframe the candles belong to: a new timeframe starts empty (old
  // candles at another size would be drawn wrong), while a longer range keeps the current ones.
  const [state, setState] = useState<{ candles: Candle[]; loading: boolean; error: boolean; key: string; range: string }>({ candles: [], loading: false, error: false, key: '', range: '' });
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    const key = `${symbol}|${tf}`;
    setState((s) => (s.key === key ? { ...s, loading: true } : { candles: [], loading: true, error: false, key, range: '' }));
    api(`/v1/candles?symbol=${encodeURIComponent(symbol)}&tf=${tf}&range=${range}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (!stop) setState({ candles: d.candles ?? [], loading: false, error: false, key, range }); })
      .catch(() => { if (!stop) setState((s) => ({ ...s, loading: false, error: true })); });
    return () => { stop = true; };
  }, [symbol, tf, range, enabled, refresh]);
  return state;
}
